import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { CHART_ROLE_COLORS, resolveSeriesColor } from '@/theme/chartPalette';
import {
  CHART_AXIS_TICK,
  CHART_TOOLTIP_CONTENT_STYLE,
  CHART_TOOLTIP_ITEM_STYLE,
  CHART_TOOLTIP_LABEL_STYLE,
  DEFAULT_CHART_HEIGHT,
  buildValueFormatter,
} from './chartStyles';

export interface CategoryBarDatum {
  key: string;
  label: string;
  value: number;
  color?: string;
}

type CategoryBarOrientation = 'columns' | 'bars';

interface CategoryBarChartProps {
  data: CategoryBarDatum[];
  orientation?: CategoryBarOrientation;
  height?: number;
  accessibleLabel: string;
  formatValue: (value: number) => string;
  valueLabel?: string;
  categoryAxisWidth?: number;
}

export function CategoryBarChart({
  data,
  orientation = 'columns',
  height = DEFAULT_CHART_HEIGHT,
  accessibleLabel,
  formatValue,
  valueLabel,
  categoryAxisWidth = 120,
}: CategoryBarChartProps) {
  const isHorizontal = orientation === 'bars';
  return (
    <div role="img" aria-label={accessibleLabel} className="w-full">
      <ResponsiveContainer width="100%" height={height}>
        <BarChart
          data={data}
          layout={isHorizontal ? 'vertical' : 'horizontal'}
          margin={{ top: 8, right: 12, bottom: 0, left: isHorizontal ? 4 : 0 }}
        >
          <CartesianGrid
            stroke={CHART_ROLE_COLORS.grid}
            strokeDasharray="3 3"
            horizontal={isHorizontal === false}
            vertical={isHorizontal}
          />
          {isHorizontal ? (
            <>
              <XAxis
                type="number"
                tickFormatter={formatValue}
                tick={CHART_AXIS_TICK}
                axisLine={false}
                tickLine={false}
                allowDecimals={false}
              />
              <YAxis
                type="category"
                dataKey="label"
                width={categoryAxisWidth}
                tick={CHART_AXIS_TICK}
                axisLine={false}
                tickLine={false}
              />
            </>
          ) : (
            <>
              <XAxis
                type="category"
                dataKey="label"
                tick={CHART_AXIS_TICK}
                axisLine={{ stroke: CHART_ROLE_COLORS.grid }}
                tickLine={false}
                interval={0}
              />
              <YAxis
                type="number"
                tickFormatter={formatValue}
                tick={CHART_AXIS_TICK}
                axisLine={false}
                tickLine={false}
                width={36}
                allowDecimals={false}
              />
            </>
          )}
          <Tooltip
            cursor={{ fill: CHART_ROLE_COLORS.grid, fillOpacity: 0.35 }}
            formatter={buildValueFormatter(formatValue)}
            contentStyle={CHART_TOOLTIP_CONTENT_STYLE}
            labelStyle={CHART_TOOLTIP_LABEL_STYLE}
            itemStyle={CHART_TOOLTIP_ITEM_STYLE}
          />
          <Bar
            dataKey="value"
            name={valueLabel}
            radius={isHorizontal ? [0, 6, 6, 0] : [6, 6, 0, 0]}
            maxBarSize={52}
          >
            {data.map(function renderCell(datum, index) {
              return <Cell key={datum.key} fill={resolveDatumColor(datum, index)} />;
            })}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function resolveDatumColor(datum: CategoryBarDatum, index: number): string {
  if (datum.color === undefined) {
    return resolveSeriesColor(index);
  }
  return datum.color;
}
